import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { ResponseDto, UserDto } from "@mimber/schema"
import type { LoginParams } from "../types"

/**
 * Organization-specific hook for user login verification
 */
export const useLogin = (
  options?: UseMutationOptions<ResponseDto<UserDto>, Error, LoginParams>
): UseMutationResult<ResponseDto<UserDto>, Error, LoginParams> => {
  guardAppUsage("mimber-organization", "useLogin")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async () => {
      const response = await server.post<ResponseDto<UserDto>>(
        "account/login",
        {} // Empty body - identityId comes from JWT token
      )
      return response.data
    },
    onSuccess: (user, variables, onMutateResult, context) => {
      options?.onSuccess?.(user, variables, onMutateResult, context)
      // Invalidate and refetch user-related queries after successful login
      queryClient.invalidateQueries({ queryKey: ["profile"] })
      queryClient.invalidateQueries({ queryKey: ["users"] })

      // Set the current user data
      queryClient.setQueryData(["current-user"], user)
    },
    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context)
      // Clear any cached user data on login failure
      queryClient.removeQueries({ queryKey: ["profile"] })
      queryClient.removeQueries({ queryKey: ["users"] })
      queryClient.removeQueries({ queryKey: ["current-user"] })
    },
  })
}
